import { useId, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { alpha, darken, type Theme } from "@mui/material/styles";
import { ChevronDown } from "lucide-react";
import { ComingSoonDialog } from "@/base/ComingSoonDialog";

// The record page's "Actions" button and menu (the account base design). Every
// action is a live menu item, as the design draws it. One that isn't built yet
// (no `onSelect`) opens a "coming soon" dialog over a backdrop instead of
// sitting greyed out in the menu: the broker learns it is planned, and nothing
// pretends to have happened.
//
// `disabled` is a DIFFERENT fact (BRK-S398): the action exists but this record
// lacks what it needs (no approved program, say). That row is greyed, and its
// `description` says why, so a disabled row is never a mystery.

/** The colour of an action's code badge. */
export type ActionTone = "accent" | "danger" | "success" | "warning";

export interface BaseFormAction {
  id: string;
  label: string;
  /** Short code shown in a badge before the label (e.g. "CLM"). */
  code?: string;
  /** The code badge's colour; defaults to the accent. */
  tone?: ActionTone;
  /** A destructive action: its label renders in the error colour. */
  danger?: boolean;
  /** Draw a divider above this item. */
  divider?: boolean;
  /** What the action does. Omitted = not built yet: it opens the
   *  coming-soon dialog. */
  onSelect?: () => void;
  /** The coming-soon dialog's message, when the default is too vague. */
  comingSoon?: string;
  /** A short line under the label: what the action does, or why it's off. */
  description?: string;
  /** Unavailable for this record — distinct from "not built yet" — or shown
   *  but not chosen while what it does is already under way (a download being
   *  fetched, BRK-S509). */
  disabled?: boolean;
}

// Badge text is darkened past the tone's main colour so the 10px code keeps
// AA contrast on its own tint.
const CODE_TONE: Record<ActionTone, (t: Theme) => { bg: string; fg: string }> =
  {
    accent: (t) => ({
      bg: alpha(t.palette.primary.main, 0.1),
      fg: t.palette.primary.dark,
    }),
    danger: (t) => ({
      bg: alpha(t.palette.error.main, 0.1),
      fg: darken(t.palette.error.main, 0.25),
    }),
    success: (t) => ({
      bg: alpha(t.palette.success.main, 0.12),
      fg: t.palette.success.dark,
    }),
    warning: (t) => ({
      bg: alpha(t.palette.warning.main, 0.14),
      fg: darken(t.palette.warning.main, 0.45),
    }),
  };

export function ActionsMenu({
  actions,
  label = "Actions",
}: {
  actions: BaseFormAction[];
  label?: string;
}) {
  const idBase = useId();
  const menuId = `${idBase}-menu`;
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  // Kept after close so the dialog's text doesn't blank during its exit fade.
  const [soon, setSoon] = useState<BaseFormAction | null>(null);
  const [soonOpen, setSoonOpen] = useState(false);

  function choose(action: BaseFormAction) {
    setAnchor(null);
    if (action.onSelect) {
      action.onSelect();
      return;
    }
    setSoon(action);
    setSoonOpen(true);
  }

  return (
    <>
      <Button
        variant="outlined"
        aria-haspopup="menu"
        aria-controls={anchor ? menuId : undefined}
        aria-expanded={anchor ? "true" : undefined}
        endIcon={<ChevronDown size={16} aria-hidden />}
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{ bgcolor: "background.paper", fontWeight: 600 }}
      >
        {label}
      </Button>
      <Menu
        id={menuId}
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: { sx: { width: 264, mt: 0.75, borderRadius: 2.25 } },
          list: { sx: { py: 0.75 } },
        }}
      >
        {actions.flatMap((a) => [
          ...(a.divider
            ? [<Divider key={`${a.id}-divider`} sx={{ my: 0.625, mx: 0.5 }} />]
            : []),
          <MenuItem
            key={a.id}
            onClick={() => choose(a)}
            disabled={a.disabled}
            sx={(t) => ({
              gap: 1.25,
              mx: 0.75,
              px: 1.375,
              py: 1.125,
              // 40px rows, and a full 44px target on a touch screen
              // (BRK-S263), as the theme gives its buttons.
              minHeight: 40,
              "@media (pointer: coarse)": { minHeight: 44 },
              borderRadius: 1,
              // MenuItem is nowrap by default; a long label wraps instead of
              // being clipped by the menu's edge.
              whiteSpace: "normal",
              color: a.danger ? "error.main" : "text.primary",
              "&:hover": {
                bgcolor: a.danger
                  ? alpha(t.palette.error.main, 0.08)
                  : "action.hover",
              },
            })}
          >
            {a.code && (
              <Box
                component="span"
                aria-hidden
                sx={(t) => ({
                  typography: "caption",
                  fontWeight: 600,
                  fontFamily: "ui-monospace, monospace",
                  px: 0.75,
                  py: 0.375,
                  borderRadius: 0.5,
                  width: 34,
                  flex: "none",
                  textAlign: "center",
                  bgcolor: CODE_TONE[a.tone ?? "accent"](t).bg,
                  color: CODE_TONE[a.tone ?? "accent"](t).fg,
                })}
              >
                {a.code}
              </Box>
            )}
            <ListItemText
              primary={a.label}
              secondary={a.description}
              sx={{ my: 0 }}
              slotProps={{
                primary: { sx: { fontWeight: 500 } },
              }}
            />
          </MenuItem>,
        ])}
      </Menu>

      <ComingSoonDialog
        open={soonOpen}
        onClose={() => setSoonOpen(false)}
        label={soon?.label ?? ""}
        message={soon?.comingSoon}
      />
    </>
  );
}
